import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, CheckSquare, Clock, CheckCircle, 
  Search, FileText, User, MapPin, 
  Briefcase, Timer, Download, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import PageLoader from '../components/PageLoader';

const TasksPage = () => {
  const { api, user, isFeatureEnabled } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [personalTasks, setPersonalTasks] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [taskDetailDialog, setTaskDetailDialog] = useState(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    deadline: '',
    assigned_to: '',
  });
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignSearchMode, setAssignSearchMode] = useState(true);  // default = search box
  const [assignSearch, setAssignSearch] = useState('');

  useEffect(() => {
    loadTasks();
  }, []);

  // Mobile header override — title "Tasks" + "+" (new) and Search
  // icons before the bell. Replaces the in-page header row.
  usePageHeader({
    title: 'Tasks',
    rightSlot: (
      <>
        <button
          onClick={() => setNewTaskDialogOpen(true)}
          className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
          aria-label="New task"
          data-testid="header-new-task-btn"
        >
          <Plus className="w-5 h-5" />
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
          aria-label="Search tasks"
          data-testid="header-search-tasks-btn"
        >
          <Search className="w-5 h-5" />
        </button>
      </>
    ),
  }, []);

  // Pull-to-refresh — page uses document scroll (min-h-screen, not a
  // fixed scroller), so we attach to window. Arrow wraps loadTasks
  // so the lookup happens at call time (loadTasks is declared lower
  // in the component → TDZ if we passed the bare reference).
  const { pullY, refreshing } = usePullToRefresh({ onRefresh: () => loadTasks() });

  // Load list of users this caller can assign tasks to (role-based on the backend)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/tasks/assignable-users');
        if (alive) setAssignableUsers(res.data?.users || []);
      } catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [api]);

  // Handle openTask query parameter to auto-open a task
  useEffect(() => {
    const openTaskId = searchParams.get('openTask') || searchParams.get('taskId');
    if (openTaskId && !loading) {
      loadTaskDetails(openTaskId);
      setSearchParams({});
    }
  }, [searchParams, loading]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const debounce = setTimeout(() => searchTasks(), 300);
      return () => clearTimeout(debounce);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const [personalRes, assignedRes, completedRes] = await Promise.all([
        api.get('/tasks/my'),
        api.get('/tasks/assigned'),
        api.get('/tasks/completed')
      ]);
      setPersonalTasks(personalRes.data.filter(t => t.status !== 'completed'));
      setAssignedTasks(assignedRes.data);
      setCompletedTasks(completedRes.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const searchTasks = async () => {
    if (searchQuery.length < 2) return;
    setSearching(true);
    try {
      const res = await api.get(`/tasks/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const loadTaskDetails = async (taskId) => {
    setTaskDetailLoading(true);
    try {
      const res = await api.get(`/tasks/${taskId}`);
      setTaskDetailDialog(res.data);
    } catch (error) {
      toast.error('Failed to load task details');
    } finally {
      setTaskDetailLoading(false);
    }
  };

  const createTask = async () => {
    if (!newTask.name.trim()) {
      toast.error('Please enter a task name');
      return;
    }
    
    try {
      const payload = {
        name: newTask.name,
        description: newTask.description,
        deadline: newTask.deadline || null,
      };
      if (newTask.assigned_to) payload.assigned_to = newTask.assigned_to;
      await api.post('/tasks', payload);
      // silent success — the task appears in the list immediately
      setNewTaskDialogOpen(false);
      setNewTask({ name: '', description: '', deadline: '', assigned_to: '' });
      loadTasks();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create task');
    }
  };

  const completeTask = async (taskId, e) => {
    e?.stopPropagation();
    try {
      await api.put(`/tasks/${taskId}`, { task_status: 'completed' });
      // silent success — the task moves to completed list
      loadTasks();
      if (taskDetailDialog?.id === taskId) {
        setTaskDetailDialog(null);
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getTaskTypeLabel = (type) => {
    switch (type) {
      case 'resume_review': return 'Resume Review';
      case 'interview_update': return 'Interview Update';
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-yellow-500';
      default: return 'bg-primary';
    }
  };

  // Task Item - Individual container box like recent calls / key priorities
  const TaskItem = ({ task, showCompleteButton = true }) => {
    const isSpecialTask = task.task_type === 'resume_review' || task.task_type === 'interview_update';
    
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 cursor-pointer hover:shadow-md transition-all"
        onClick={() => isSpecialTask ? loadTaskDetails(task.id) : setTaskDetailDialog(task)}
        data-testid={`task-${task.id}`}
      >
        {/* Status indicator / Complete button */}
        {showCompleteButton && task.status !== 'completed' ? (
          <button
            className="w-9 h-9 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:border-primary hover:bg-primary/10 flex items-center justify-center flex-shrink-0 transition-colors"
            onClick={(e) => completeTask(task.id, e)}
            data-testid={`complete-task-${task.id}`}
          >
            <CheckCircle className="w-4 h-4 opacity-30 hover:opacity-100 text-primary" />
          </button>
        ) : (
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            task.status === 'completed' ? 'bg-green-500/20' : 'bg-primary/20'
          }`}>
            {task.status === 'completed' ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <div className={`w-3 h-3 rounded-full ${getStatusColor(task.status)}`} />
            )}
          </div>
        )}
        
        {/* Task content */}
        <div className="flex-1 min-w-0">
          <p className={`font-medium text-sm truncate ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
            {task.name}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            {task.deadline && (
              <p className="text-xs text-muted-foreground">
                Due: {new Date(task.deadline).toLocaleDateString()}
              </p>
            )}
            {task.related_user_name && (
              <p className="text-xs text-muted-foreground truncate">
                • {task.related_user_name}
              </p>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {getTaskTypeLabel(task.task_type) && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {getTaskTypeLabel(task.task_type)}
            </Badge>
          )}
          {task.countdown_days !== undefined && task.countdown_days !== null && (
            <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px] px-1.5 py-0">
              <Timer className="w-3 h-3 mr-0.5" />
              {task.countdown_days}d
            </Badge>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <PageLoader label="Loading tasks..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 md:pt-2 pb-24" data-testid="tasks-page">
      <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} />
      <div className="max-w-2xl md:portrait:max-w-4xl mx-auto space-y-6">
        {/* Personal Tasks Section */}
        {isFeatureEnabled('tasks_personal') && (
        <section className="space-y-3" data-testid="section-personal">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <CheckSquare className="w-4 h-4" />
            Personal Tasks
            <Badge variant="outline" className="text-xs ml-auto">{personalTasks.length}</Badge>
          </h2>
          {personalTasks.length === 0 ? (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-center text-muted-foreground">
              <CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No personal tasks</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {personalTasks.map(task => (
                <TaskItem key={task.id} task={task} showCompleteButton={true} />
              ))}
            </div>
          )}
        </section>
        )}

        {/* Assigned to Me Section */}
        {isFeatureEnabled('tasks_assigned') && (
        <section className="space-y-3" data-testid="section-assigned">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <User className="w-4 h-4" />
            Assigned to Me
            <Badge variant="outline" className="text-xs ml-auto">{assignedTasks.length}</Badge>
          </h2>
          {assignedTasks.length === 0 ? (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-center text-muted-foreground">
              <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No assigned tasks</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {assignedTasks.map(task => (
                <TaskItem key={task.id} task={task} showCompleteButton={true} />
              ))}
            </div>
          )}
        </section>
        )}

        {/* Completed Tasks Section */}
        {isFeatureEnabled('tasks_completed') && (
        <section className="space-y-3" data-testid="section-completed">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Completed Tasks
            <Badge variant="outline" className="text-xs ml-auto">{completedTasks.length}</Badge>
          </h2>
          {completedTasks.length === 0 ? (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-center text-muted-foreground">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No completed tasks yet</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {completedTasks.slice(0, 5).map(task => (
                <TaskItem key={task.id} task={task} showCompleteButton={false} />
              ))}
              {completedTasks.length > 5 && (
                <p className="text-xs text-center text-muted-foreground pt-2">
                  + {completedTasks.length - 5} more completed tasks
                </p>
              )}
            </div>
          )}
        </section>
        )}
      </div>

      {/* New Task Dialog - AI Personality Popup Style */}
      <Dialog open={newTaskDialogOpen} onOpenChange={setNewTaskDialogOpen}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Create New Task</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Add a personal task to your list</p>
          </DialogHeader>
          <div className="px-4 pb-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="task-name" className="text-xs font-medium">Task Name *</Label>
              <Input
                id="task-name"
                placeholder="What do you need to do?"
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                className="h-9 rounded-xl"
                data-testid="new-task-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-desc" className="text-xs font-medium">Description</Label>
              <Input
                id="task-desc"
                placeholder="Optional details"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                className="h-9 rounded-xl"
                data-testid="new-task-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-deadline" className="text-xs font-medium">Deadline</Label>
              <Input
                id="task-deadline"
                type="datetime-local"
                value={newTask.deadline}
                onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                className="h-9 rounded-xl"
                style={{ fontSize: '16px' }}
                data-testid="new-task-deadline"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-assigned-to" className="text-xs font-medium">Assign to</Label>
              <div className="flex gap-2 items-start">
                {assignSearchMode ? (
                  <div className="flex-1 min-w-0">
                    <Input
                      id="task-assigned-to-search"
                      type="text"
                      placeholder={(() => {
                        const sel = assignableUsers.find((u) => u.id === newTask.assigned_to);
                        return sel ? sel.name : 'Search a teammate (or leave blank to assign to yourself)';
                      })()}
                      value={assignSearch}
                      onChange={(e) => setAssignSearch(e.target.value)}
                      className="h-9 rounded-xl"
                      style={{ fontSize: '16px' }}
                      data-testid="new-task-assigned-search"
                    />
                    {assignSearch.trim() && (
                      <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border bg-background shadow-sm" data-testid="assign-search-results">
                        {assignableUsers
                          .filter((u) => !u.is_self && (u.name || '').toLowerCase().includes(assignSearch.toLowerCase()))
                          .slice(0, 12)
                          .map((u) => (
                            <button
                              type="button"
                              key={u.id}
                              onClick={() => {
                                setNewTask({ ...newTask, assigned_to: u.id });
                                setAssignSearch('');
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                              data-testid={`assign-search-result-${u.id}`}
                            >
                              {u.name}<span className="ml-2 text-xs text-muted-foreground">{u.role}</span>
                            </button>
                          ))}
                        {assignableUsers.filter((u) => !u.is_self && (u.name || '').toLowerCase().includes(assignSearch.toLowerCase())).length === 0 && (
                          <p className="px-3 py-2 text-xs text-muted-foreground">No matches.</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <select
                    id="task-assigned-to"
                    value={newTask.assigned_to}
                    onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                    className="h-9 flex-1 rounded-xl border bg-background px-2 text-sm"
                    style={{ fontSize: '16px' }}
                    data-testid="new-task-assigned-to"
                  >
                    <option value="">Myself</option>
                    {assignableUsers.filter((u) => !u.is_self).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}{u.role ? ` (${u.role})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setAssignSearchMode((v) => !v);
                    setAssignSearch('');
                  }}
                  className="h-9 px-3 rounded-xl border bg-background text-xs font-semibold flex-shrink-0 self-start"
                  data-testid="new-task-assign-mode-toggle"
                  title={assignSearchMode ? 'Switch to dropdown' : 'Switch to search'}
                >
                  {assignSearchMode ? 'List' : 'Search'}
                </button>
              </div>
              {newTask.assigned_to && (
                <p className="text-[11px] text-muted-foreground">
                  Assigning to: <span className="font-semibold">{assignableUsers.find((u) => u.id === newTask.assigned_to)?.name || 'selected user'}</span>
                  <button type="button" onClick={() => setNewTask({ ...newTask, assigned_to: '' })} className="ml-2 underline">clear</button>
                </p>
              )}
              {assignableUsers.length === 0 && (
                <p className="text-[10px] text-muted-foreground">You can only assign tasks to yourself.</p>
              )}
            </div>
            <Button 
              className="w-full h-10 rounded-xl bg-[#586D52] hover:bg-[#4a5d46] mt-2"
              onClick={createTask}
              data-testid="create-task-btn"
            >
              Create Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search Dialog - AI Personality Popup Style */}
      <Dialog open={searchOpen} onOpenChange={(open) => {
        setSearchOpen(open);
        if (!open) {
          setSearchQuery('');
          setSearchResults([]);
        }
      }}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Search Tasks</DialogTitle>
          </DialogHeader>
          <div className="px-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by task name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 rounded-xl"
                data-testid="search-tasks-input"
                autoFocus
              />
            </div>
          </div>
          <ScrollArea className="max-h-[40vh] px-4 pb-4">
            {searching ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-1.5">
                {searchResults.map(task => (
                  <TaskItem key={task.id} task={task} showCompleteButton={true} />
                ))}
              </div>
            ) : searchQuery.length >= 2 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">No tasks found</p>
            ) : (
              <p className="text-center text-muted-foreground py-8 text-sm">Enter at least 2 characters to search</p>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog - AI Personality Popup Style */}
      <Dialog open={!!taskDetailDialog} onOpenChange={(open) => !open && setTaskDetailDialog(null)}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {taskDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : taskDetailDialog && (
            <>
              <DialogHeader className="p-4 pb-2">
                <div className="flex items-center gap-2">
                  {taskDetailDialog.task_type === 'resume_review' && <FileText className="w-5 h-5 text-blue-500" />}
                  {taskDetailDialog.task_type === 'interview_update' && <Clock className="w-5 h-5 text-amber-500" />}
                  {!taskDetailDialog.task_type && <CheckSquare className="w-5 h-5 text-[#586D52]" />}
                  <DialogTitle className="text-lg font-bold">{taskDetailDialog.name}</DialogTitle>
                </div>
                {taskDetailDialog.description && (
                  <p className="text-xs text-muted-foreground mt-1">{taskDetailDialog.description}</p>
                )}
              </DialogHeader>

              <div className="px-4 pb-4 space-y-3">
                {/* Task Info */}
                {(taskDetailDialog.deadline || taskDetailDialog.countdown_days !== undefined) && (
                  <div className="bg-muted/30 rounded-xl p-3 space-y-2">
                    {taskDetailDialog.deadline && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>Due: {new Date(taskDetailDialog.deadline).toLocaleDateString()}</span>
                      </div>
                    )}
                    {taskDetailDialog.countdown_days !== undefined && taskDetailDialog.countdown_days !== null && (
                      <div className="flex items-center gap-2 text-sm text-amber-600">
                        <Timer className="w-4 h-4" />
                        <span>Response needed within {taskDetailDialog.countdown_days} days</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Related User Info (for special tasks) */}
                {taskDetailDialog.related_user && (
                  <div className="bg-muted/30 rounded-xl p-3 space-y-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={taskDetailDialog.related_user.profile_picture} />
                        <AvatarFallback className="text-sm bg-primary/10 text-primary">
                          {taskDetailDialog.related_user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{taskDetailDialog.related_user.full_name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="capitalize text-[10px] px-1.5 py-0">{taskDetailDialog.related_user.role}</Badge>
                          {taskDetailDialog.related_user.state && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="w-3 h-3" />
                              {taskDetailDialog.related_user.state}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {taskDetailDialog.related_user.industry && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Briefcase className="w-3.5 h-3.5" />
                        {taskDetailDialog.related_user.industry}
                        {taskDetailDialog.related_user.industry_domain && ` • ${taskDetailDialog.related_user.industry_domain}`}
                      </div>
                    )}
                  </div>
                )}

                {/* Resume Preview */}
                {taskDetailDialog.related_user_resume && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" />
                      Resume
                    </Label>
                    <div className="bg-muted/30 rounded-xl p-3">
                      {taskDetailDialog.related_user_resume.startsWith('data:application/pdf') ? (
                        <div className="space-y-2">
                          <object
                            data={taskDetailDialog.related_user_resume}
                            type="application/pdf"
                            className="w-full h-48 border rounded-lg bg-white"
                          >
                            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                              <FileText className="w-8 h-8 mb-2" />
                              <p className="text-xs">PDF preview not available</p>
                            </div>
                          </object>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="w-full rounded-xl"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = taskDetailDialog.related_user_resume;
                              link.download = `resume_${taskDetailDialog.related_user?.full_name?.replace(/\s+/g, '_') || 'download'}.pdf`;
                              link.click();
                            }}
                          >
                            <Download className="w-3.5 h-3.5 mr-1.5" />
                            Download Resume
                          </Button>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground text-center py-4">Resume preview not available</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-2 pt-2">
                  {taskDetailDialog.task_type === 'interview_update' && user?.role === 'admin' ? (
                    <>
                      <Button
                        variant="outline"
                        className="w-full h-10 rounded-xl border-red-500/50 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={async () => {
                          try {
                            await api.post('/users/approve-interview', { user_id: taskDetailDialog.related_user_id, approved: false });
                            await completeTask(taskDetailDialog.id);
                          } catch (error) {
                            toast.error('Failed to decline progression');
                          }
                        }}
                      >
                        Decline Progression
                      </Button>
                      <Button
                        className="w-full h-10 rounded-xl bg-[#586D52] hover:bg-[#4a5d46]"
                        onClick={async () => {
                          try {
                            await api.post('/users/approve-interview', { user_id: taskDetailDialog.related_user_id, approved: true });
                            await completeTask(taskDetailDialog.id);
                          } catch (error) {
                            toast.error('Failed to approve progression');
                          }
                        }}
                      >
                        Approve Progression
                      </Button>
                    </>
                  ) : taskDetailDialog.task_type === 'resume_review' ? (
                    <Button
                      className="w-full h-10 rounded-xl bg-[#586D52] hover:bg-[#4a5d46]"
                      onClick={() => completeTask(taskDetailDialog.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Reviewed
                    </Button>
                  ) : taskDetailDialog.status !== 'completed' ? (
                    <Button
                      className="w-full h-10 rounded-xl bg-[#586D52] hover:bg-[#4a5d46]"
                      onClick={() => completeTask(taskDetailDialog.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete Task
                    </Button>
                  ) : (
                    <div className="bg-green-500/10 rounded-xl p-3 flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Task Completed</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
